/**
 * SessionVoiceCallModal — full-screen "video call" overlay for any
 * training session (student, lecturer review, or therapist practice)
 * when Advanced Voice Mode is active.
 *
 * Shows the persona portrait large (as if the patient is on a video call).
 * Falls back to a camera-off avatar when no portrait is available.
 *
 * Wraps the existing VoiceLivePanel so we don't duplicate the audio
 * plumbing — the panel still owns the WebSocket / mic / TTS lifecycle;
 * this component just gives it a larger, more clinical chrome.
 *
 * Layout uses a plain flex column (no absolute/z-index) so the End-Call
 * button is always clickable and never sits behind the inner panel.
 */

import React, { useEffect } from 'react';
import {
  Dialog, Box, Typography, IconButton, Avatar, Tooltip, Stack, Button,
} from '@mui/material';
import VideocamOffIcon from '@mui/icons-material/VideocamOff';
import CallEndIcon from '@mui/icons-material/CallEnd';
import AssessmentIcon from '@mui/icons-material/Assessment';
import FlagIcon from '@mui/icons-material/Flag';
import VoiceLivePanel from '../chat/VoiceLivePanel';
import { holdSessionAwake } from '../../services/sessionManager';

const callToolSx = {
  borderRadius: 4,
  px: 2,
  color: 'white',
  borderColor: 'rgba(255,255,255,0.4)',
  '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.08)' },
  '&.Mui-disabled': { color: 'rgba(255,255,255,0.35)', borderColor: 'rgba(255,255,255,0.15)' },
};

export default function SessionVoiceCallModal({

  open,
  personaName,
  personaImageUrl,
  onEndCall,
  // Clinical tools reachable without leaving the call. The call covers the
  // whole screen, so the session header's buttons sit behind it; anything the
  // therapist needs mid-call has to be offered here. Omit a handler to hide
  // its button.
  onRequestAssessment,
  assessmentsHidden = false,
  assessmentsUnavailable = false,
  onSuggestGoal,
  // VoiceLivePanel passthrough props
  voiceLivePanelRef,
  trainingSocket,
  trainingSessionId,
}) {
  // An open voice call keeps the session awake without input (security batch 2).
  useEffect(() => (open ? holdSessionAwake('training_voice') : undefined), [open]);
  const handleClose = (_e, reason) => {
    // Don't dismiss on backdrop click or Esc — accidental click on a
    // video call would silently terminate the session and lose context.
    // Require an explicit End-Call press.
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
    onEndCall?.();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullScreen
      PaperProps={{
        sx: {
          bgcolor: '#0b0d10',
          color: 'white',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      {/* Top bar */}
      <Box
        sx={{
          flex: '0 0 auto',
          px: 2, py: 1.5,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: 2,
          borderBottom: '1px solid rgba(255,255,255,0.06)',
          bgcolor: 'rgba(0,0,0,0.4)',
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              width: 10, height: 10, borderRadius: '50%',
              bgcolor: 'error.main',
              animation: 'recPulse 1.6s ease-in-out infinite',
              '@keyframes recPulse': {
                '0%, 100%': { opacity: 1 },
                '50%': { opacity: 0.4 },
              },
            }}
          />
          <Typography variant="caption" sx={{ letterSpacing: 1, opacity: 0.85 }}>
            LIVE PRACTICE CALL
          </Typography>
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center">
          {onRequestAssessment && !assessmentsHidden && (
            <Tooltip
              title={assessmentsUnavailable
                ? 'This scenario has no assessments to give'
                : 'Give an assessment without ending the call'}
            >
              <span>
                <Button
                  variant="outlined"
                  startIcon={<AssessmentIcon />}
                  onClick={onRequestAssessment}
                  disabled={assessmentsUnavailable}
                  data-testid="call-request-assessment"
                  sx={callToolSx}
                >
                  Request Assessment
                </Button>
              </span>
            </Tooltip>
          )}
          {onSuggestGoal && (
            <Tooltip title="Suggest a goal without ending the call">
              <Button
                variant="outlined"
                startIcon={<FlagIcon />}
                onClick={onSuggestGoal}
                data-testid="call-suggest-goal"
                sx={callToolSx}
              >
                Suggest Goal
              </Button>
            </Tooltip>
          )}
          <Button
            variant="contained"
            color="error"
            startIcon={<CallEndIcon />}
            onClick={onEndCall}
            sx={{ borderRadius: 4, px: 2 }}
          >
            End Call
          </Button>
        </Stack>
      </Box>

      {/* Patient "video" panel */}
      <Box
        sx={{
          flex: '1 1 auto',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          overflow: 'hidden',
          minHeight: 0,
        }}
      >
        {personaImageUrl ? (
          <>
            {/* Blurred backdrop using the portrait so the dark canvas
                doesn't feel empty on ultra-wide screens. */}
            <Box
              sx={{
                position: 'absolute', inset: 0,
                backgroundImage: `url(${personaImageUrl})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                filter: 'blur(40px) brightness(0.4)',
                transform: 'scale(1.1)',
                pointerEvents: 'none',
              }}
            />
            <Avatar
              src={personaImageUrl}
              alt={personaName || 'Patient'}
              variant="rounded"
              sx={{
                width: { xs: 220, sm: 320, md: 420 },
                height: { xs: 220, sm: 320, md: 420 },
                boxShadow: '0 24px 64px rgba(0,0,0,0.6)',
                position: 'relative',
              }}
            />
          </>
        ) : (
          <Box
            sx={{
              width: { xs: 220, sm: 300 },
              height: { xs: 220, sm: 300 },
              borderRadius: 2,
              bgcolor: '#1a1d22',
              border: '2px solid rgba(255,255,255,0.1)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1.5,
            }}
          >
            <VideocamOffIcon sx={{ fontSize: 80, opacity: 0.5 }} />
            <Typography variant="body2" sx={{ opacity: 0.6 }}>Camera off</Typography>
          </Box>
        )}

        {/* Patient name caption */}
        <Box
          sx={{
            mt: 2,
            px: 2, py: 0.75,
            bgcolor: 'rgba(0,0,0,0.55)',
            borderRadius: 1,
            backdropFilter: 'blur(4px)',
            position: 'relative',
          }}
        >
          <Typography variant="subtitle1" fontWeight={500}>
            {personaName || 'Patient'}
          </Typography>
        </Box>
      </Box>

      {/* Voice live panel — controls (mute, status, transcript, interrupt)
          along the bottom in a more compact layout. */}
      <Box
        sx={{
          flex: '0 0 auto',
          bgcolor: 'rgba(0,0,0,0.7)',
          backdropFilter: 'blur(8px)',
          borderTop: '1px solid rgba(255,255,255,0.1)',
        }}
      >
        {trainingSocket && trainingSessionId ? (
          <VoiceLivePanel
            ref={voiceLivePanelRef}
            socket={trainingSocket}
            conversationId={trainingSessionId}
            onClose={onEndCall}
            eventPrefix="training:voice:"
          />
        ) : (
          <Box sx={{ p: 2, textAlign: 'center', opacity: 0.6 }}>
            <Typography variant="caption">Voice channel unavailable.</Typography>
          </Box>
        )}
      </Box>
    </Dialog>
  );
}
